import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";
import {
  createPosition,
  fetchPositionHolders,
  Position,
  PositionHolder,
  setPositionPermissions,
  setPositionRoles,
  updatePosition,
} from "services/positions.service";
import { fetchPermissionCatalogue } from "services/permissions.service";
import { PermissionCatalogueEntry } from "types/Permissions";
import PermissionPicker from "components/permission-picker/PermissionPicker";
import PermissionChips from "components/permission-picker/PermissionChips";
import { fetchWorkSecurityAreas, WorkSecurityArea } from "services/work-security-areas.service";
import { fetchWorkSecurityRoles, WorkSecurityRole } from "services/work-security.service";
import GroupedRoleCheckboxes from "./GroupedRoleCheckboxes";
import PositionRoleChips from "./PositionRoleChips";
import { mergeEditableRoleIds, sameIdSet } from "./positions-helpers";
import { describePositionsError } from "./positions-errors";

/**
 * Create / edit one catalogue position: names plus its pareigybės, grouped by
 * sphere. Only the spheres the user manages (Master, or a responsible of the
 * sphere — the backend's canManageArea) are editable; links in other spheres
 * are shown read-only and sent back untouched.
 *
 * The position also gives rights to everyone who holds it. Only a Master
 * edits them; anyone else sees the list.
 */

type Props = {
  companyId: string;
  /** null = create a new position. */
  position: Position | null;
  /** Master: may change the rights the position gives. */
  canEditPermissions: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type ManagedArea = { area: WorkSecurityArea; roles: WorkSecurityRole[] };

export default function PositionEditDialog({
  companyId,
  position,
  canEditPermissions,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [nameLT, setNameLT] = useState(position?.nameLT || "");
  const [nameEN, setNameEN] = useState(position?.nameEN || "");
  const [nameLTFemale, setNameLTFemale] = useState(position?.nameLTFemale || "");
  // The position as saved on the server — becomes non-null right after a
  // create, turning the dialog into edit mode for that id.
  const [current, setCurrent] = useState<Position | null>(position);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [managed, setManaged] = useState<ManagedArea[] | null>(null);
  const [checkedRoleIds, setCheckedRoleIds] = useState<string[]>([]);
  const [holders, setHolders] = useState<PositionHolder[] | null>(null);
  const [holdersFailed, setHoldersFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // The rights the position gives: the ticked ones, and what the server has now
  // (so a retry after a failed save sends only what is still unsaved).
  const [permissionKeys, setPermissionKeys] = useState<string[]>(position?.permissions || []);
  const [savedPermissionKeys, setSavedPermissionKeys] = useState<string[]>(position?.permissions || []);
  // The list of rights to offer; null until loaded, so Save sends nothing for rights it could not show.
  const [permissionCatalogue, setPermissionCatalogue] = useState<PermissionCatalogueEntry[] | null>(null);
  const [permissionsLoadFailed, setPermissionsLoadFailed] = useState(false);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);

  useEffect(() => {
    if (!canEditPermissions) return undefined;
    let cancelled = false;
    fetchPermissionCatalogue(companyId)
      .then((catalogue) => {
        if (!cancelled) setPermissionCatalogue(catalogue.filter((right) => right.positionGrantable));
      })
      .catch(() => {
        if (!cancelled) setPermissionsLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, canEditPermissions]);

  // Spheres the user manages and their active roles. Anyone without Tvarkos
  // access gets a refusal here — that simply means nothing is editable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [areasData, roles] = await Promise.all([
          fetchWorkSecurityAreas(companyId),
          fetchWorkSecurityRoles(companyId),
        ]);
        if (cancelled) return;
        const areaList = areasData.areas || [];
        const list: ManagedArea[] = areaList.map((area) => ({
          area,
          roles: (Array.isArray(roles) ? roles : [])
            .filter((role) => role.area === area.id && !role.isArchived)
            .sort((a, b) => a.name.localeCompare(b.name, "lt")),
        }));
        setManaged(list);
        const managedRoleIds = new Set(list.flatMap((item) => item.roles.map((role) => role.id)));
        setCheckedRoleIds(
          (position?.roles || []).map((role) => role.id).filter((id) => managedRoleIds.has(id))
        );
      } catch {
        if (!cancelled) setManaged([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, position]);

  useEffect(() => {
    if (!position) return undefined;
    let cancelled = false;
    fetchPositionHolders(companyId, position.id)
      .then((rows) => {
        if (!cancelled) setHolders(rows);
      })
      .catch(() => {
        if (!cancelled) setHoldersFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, position]);

  const managedAreaIds = useMemo(
    () => new Set((managed || []).map((item) => item.area.id)),
    [managed]
  );
  const readOnlyRoles = (position?.roles || []).filter(
    (role) => !role.areaId || !managedAreaIds.has(role.areaId)
  );
  const showAreaNames = (managed || []).length > 1 || readOnlyRoles.length > 0;

  const toggleRole = (roleId: string) =>
    setCheckedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );

  const save = async () => {
    if (!nameLT.trim()) {
      toast.error(t("positions_name_lt_required"));
      return;
    }
    setSaving(true);
    setRolesError(null);
    setPermissionsError(null);
    const names = {
      nameLT: nameLT.trim(),
      nameLTFemale: nameLTFemale.trim() || null,
      nameEN: nameEN.trim() || null,
    };
    // Step 1: the position itself. Once created, the dialog holds on to it
    // (`current`), so a retry after a failed roles save never creates a
    // second row (POSITION_NAME_TAKEN + an orphan).
    let saved = current;
    try {
      if (!saved) {
        saved = await createPosition(companyId, names);
        setCurrent(saved);
      } else if (
        names.nameLT !== saved.nameLT ||
        names.nameLTFemale !== (saved.nameLTFemale || null) ||
        names.nameEN !== (saved.nameEN || null)
      ) {
        await updatePosition(companyId, saved.id, names);
        saved = { ...saved, ...names };
        setCurrent(saved);
      }
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
      setSaving(false);
      return;
    }
    // Step 2: its pareigybės — a separate request; a failure is shown inline
    // and Save retries only this part.
    try {
      if (managed) {
        const nextRoleIds = mergeEditableRoleIds(saved.roles || [], managedAreaIds, checkedRoleIds);
        const currentRoleIds = (saved.roles || []).map((role) => role.id);
        if (!sameIdSet(nextRoleIds, currentRoleIds)) {
          await setPositionRoles(companyId, saved.id, nextRoleIds);
        }
      }
    } catch (e: any) {
      setRolesError(describePositionsError(e, t));
      setSaving(false);
      return;
    }
    // Step 3: the rights it gives (Master only) — also its own request and inline error.
    try {
      if (permissionCatalogue && !sameIdSet(permissionKeys, savedPermissionKeys)) {
        await setPositionPermissions(companyId, saved.id, permissionKeys);
        setSavedPermissionKeys(permissionKeys);
      }
      toast.success(t("positions_saved"));
      onSaved();
    } catch (e: any) {
      setPermissionsError(describePositionsError(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? () => {} : onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-0 flex items-start justify-center p-2 sm:p-6">
        <DialogPanel className="mt-2 flex max-h-[85dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-zinc-950/10 sm:mt-8">
          <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
              {current ? t("positions_edit_title") : t("positions_new_title")}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label={t("close")}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 max-md:p-2.5"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">
                  {t("positions_name_lt")} <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  value={nameLT}
                  maxLength={255}
                  onChange={(event) => setNameLT(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">{t("positions_name_lt_female")}</span>
                <input
                  type="text"
                  value={nameLTFemale}
                  maxLength={255}
                  placeholder={t("positions_name_lt_female_placeholder")}
                  onChange={(event) => setNameLTFemale(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">{t("positions_name_en")}</span>
                <input
                  type="text"
                  value={nameEN}
                  maxLength={255}
                  onChange={(event) => setNameEN(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
                />
              </label>
            </div>
            {position && position.holdersCount > 0 && (
              <p className="text-xs text-gray-500">{t("positions_rename_hint")}</p>
            )}

            <section>
              <h3 className="text-sm font-semibold text-gray-900">{t("positions_roles_title")}</h3>
              <p className="mt-0.5 text-xs text-gray-500">{t("positions_roles_hint")}</p>
              {rolesError && (
                <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {current && !position ? `${t("positions_created_roles_failed")} ` : ""}
                  {rolesError}
                </p>
              )}
              {managed === null ? (
                <p className="mt-2 text-sm text-gray-500">{t("loading")}</p>
              ) : (
                <div className="mt-2 space-y-3">
                  {managed.map(({ area, roles }) => (
                    <div key={area.id}>
                      {showAreaNames && (
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {area.name}
                        </p>
                      )}
                      {roles.length === 0 ? (
                        <p className="text-sm text-gray-400">{t("positions_area_no_roles")}</p>
                      ) : (
                        <GroupedRoleCheckboxes
                          roles={roles}
                          checkedRoleIds={checkedRoleIds}
                          onToggleRole={toggleRole}
                          onSetCheckedRoleIds={setCheckedRoleIds}
                        />
                      )}
                    </div>
                  ))}
                  {readOnlyRoles.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs text-gray-500">{t("positions_roles_read_only")}</p>
                      <PositionRoleChips roles={readOnlyRoles} />
                    </div>
                  )}
                  {managed.length === 0 && readOnlyRoles.length === 0 && (
                    <p className="text-sm text-gray-400">{t("positions_roles_none_manageable")}</p>
                  )}
                </div>
              )}
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-900">{t("positions_permissions_title")}</h3>
              <p className="mt-0.5 text-xs text-gray-500">{t("positions_permissions_hint")}</p>
              {canEditPermissions ? (
                <>
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    {t("positions_permissions_applies_at_once")}
                  </p>
                  {permissionsError && (
                    <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {permissionsError}
                    </p>
                  )}
                  <div className="mt-2">
                    {permissionCatalogue ? (
                      <PermissionPicker
                        catalogue={permissionCatalogue}
                        value={permissionKeys}
                        canAssignMaster={false}
                        onChange={setPermissionKeys}
                      />
                    ) : (
                      <p className={permissionsLoadFailed ? "text-sm text-red-700" : "text-sm text-gray-500"}>
                        {permissionsLoadFailed ? t("positions_load_failed") : t("loading")}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-2">
                  {permissionKeys.length === 0 ? (
                    <p className="text-sm text-gray-400">{t("positions_permissions_none")}</p>
                  ) : (
                    <PermissionChips permissions={permissionKeys.map((key) => ({ key }))} />
                  )}
                </div>
              )}
            </section>

            {position && (
              <section>
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("positions_holders_title")} ({position.holdersCount})
                </h3>
                {holdersFailed ? (
                  <p className="mt-1 text-sm text-red-700">{t("positions_load_failed")}</p>
                ) : holders === null ? (
                  <p className="mt-1 text-sm text-gray-500">{t("loading")}</p>
                ) : holders.length === 0 ? (
                  <p className="mt-1 text-sm text-gray-400">{t("positions_no_holders")}</p>
                ) : (
                  <p className="mt-1 text-sm text-gray-700">
                    {holders.map((holder) => holder.name).join(", ")}
                  </p>
                )}
              </section>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 max-md:min-h-11"
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || managed === null}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 max-md:min-h-11"
            >
              {t("save")}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
